import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Pill, Plus, Check, Clock, Trash2, RefreshCw, Syringe } from 'lucide-react';
import { medicationsAPI } from '../services/api';
import { toast } from '../hooks/use-toast';

const MedicationReminders = () => {
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newMed, setNewMed] = useState({
    medication_name: '',
    dosage: '',
    schedule_time: '08:00',
    frequency: 'Daily',
    type: 'medication',
    insulin_type: null
  });

  const fetchMedications = async () => {
    setLoading(true);
    try {
      const data = await medicationsAPI.getAll();
      setMedications(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch medications",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMedications();
  }, []);

  const handleAddMedication = async () => {
    if (!newMed.medication_name || !newMed.dosage) {
      toast({
        title: "Error",
        description: "Please fill in medication name and dosage",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);
    try {
      const medData = {
        medication_name: newMed.medication_name,
        dosage: newMed.dosage,
        schedule_time: newMed.schedule_time,
        frequency: newMed.frequency,
        type: newMed.type,
        insulin_type: newMed.type === 'insulin' ? newMed.insulin_type : null
      };

      const createdMed = await medicationsAPI.create(medData);
      setMedications([...medications, createdMed]);
      setDialogOpen(false);
      setNewMed({
        medication_name: '',
        dosage: '',
        schedule_time: '08:00',
        frequency: 'Daily',
        type: 'medication',
        insulin_type: null
      });

      toast({
        title: "Success!",
        description: "Medication reminder added"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to add medication",
        variant: "destructive"
      });
    }
    setSubmitting(false);
  };

  const handleToggleTaken = async (med) => {
    try {
      const updated = await medicationsAPI.update(med.id, { taken_today: !med.taken_today });
      setMedications(medications.map(m => m.id === med.id ? updated : m));
      toast({
        title: updated.taken_today ? "Marked as taken" : "Marked as not taken",
        description: med.medication_name
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update medication",
        variant: "destructive"
      });
    }
  };

  const handleDeleteMedication = async (medId) => {
    try {
      await medicationsAPI.delete(medId);
      setMedications(medications.filter(m => m.id !== medId));
      toast({
        title: "Deleted",
        description: "Medication reminder removed"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete medication",
        variant: "destructive"
      });
    }
  };

  const regularMeds = medications.filter(m => m.type === 'medication');
  const insulinMeds = medications.filter(m => m.type === 'insulin');
  const takenCount = medications.filter(m => m.taken_today).length;
  const adherencePercent = medications.length > 0 ? Math.round((takenCount / medications.length) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Medication Reminders</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Track your medications and insulin</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchMedications} className="dark:border-gray-600 dark:text-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 shadow-lg hover:shadow-xl transition-all">
                <Plus className="w-4 h-4 mr-2" />
                Add Medication
              </Button>
            </DialogTrigger>
            <DialogContent className="dark:bg-gray-800">
              <DialogHeader>
                <DialogTitle className="dark:text-white">Add Medication Reminder</DialogTitle>
                <DialogDescription className="dark:text-gray-400">
                  Set up a new medication or insulin reminder
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label className="dark:text-gray-300">Type</Label>
                  <Select
                    value={newMed.type}
                    onValueChange={(value) => setNewMed({ ...newMed, type: value })}
                  >
                    <SelectTrigger className="dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-gray-700">
                      <SelectItem value="medication">Medication</SelectItem>
                      <SelectItem value="insulin">Insulin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="dark:text-gray-300">Name</Label>
                  <Input
                    type="text"
                    placeholder={newMed.type === 'insulin' ? "e.g., Rapid-Acting Insulin" : "e.g., Metformin"}
                    value={newMed.medication_name}
                    onChange={(e) => setNewMed({ ...newMed, medication_name: e.target.value })}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="dark:text-gray-300">Dosage</Label>
                    <Input
                      type="text"
                      placeholder={newMed.type === 'insulin' ? "e.g., 10 units" : "e.g., 500mg"}
                      value={newMed.dosage}
                      onChange={(e) => setNewMed({ ...newMed, dosage: e.target.value })}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="dark:text-gray-300">Time</Label>
                    <Input
                      type="time"
                      value={newMed.schedule_time}
                      onChange={(e) => setNewMed({ ...newMed, schedule_time: e.target.value })}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="dark:text-gray-300">Frequency</Label>
                  <Select
                    value={newMed.frequency}
                    onValueChange={(value) => setNewMed({ ...newMed, frequency: value })}
                  >
                    <SelectTrigger className="dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-gray-700">
                      <SelectItem value="Daily">Daily</SelectItem>
                      <SelectItem value="Twice Daily">Twice Daily</SelectItem>
                      <SelectItem value="Before Breakfast">Before Breakfast</SelectItem>
                      <SelectItem value="Before Lunch">Before Lunch</SelectItem>
                      <SelectItem value="Before Dinner">Before Dinner</SelectItem>
                      <SelectItem value="Bedtime">Bedtime</SelectItem>
                      <SelectItem value="As Needed">As Needed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newMed.type === 'insulin' && (
                  <div className="space-y-2">
                    <Label className="dark:text-gray-300">Insulin Type</Label>
                    <Select
                      value={newMed.insulin_type || 'rapid'}
                      onValueChange={(value) => setNewMed({ ...newMed, insulin_type: value })}
                    >
                      <SelectTrigger className="dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-gray-700">
                        <SelectItem value="rapid">Rapid-Acting</SelectItem>
                        <SelectItem value="long">Long-Acting</SelectItem>
                        <SelectItem value="intermediate">Intermediate-Acting</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button 
                  onClick={handleAddMedication} 
                  className="w-full bg-gradient-to-r from-emerald-500 to-blue-500"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Add Reminder'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="shadow-md border-0 dark:bg-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Medications</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{medications.length}</p>
              </div>
              <div className="w-12 h-12 bg-violet-100 dark:bg-violet-900/30 rounded-full flex items-center justify-center">
                <Pill className="w-6 h-6 text-violet-600 dark:text-violet-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-md border-0 dark:bg-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Taken Today</p>
                <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{takenCount}/{medications.length}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-md border-0 dark:bg-gray-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Adherence</p>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{adherencePercent}%</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Regular Medications */}
      <Card className="shadow-md border-0 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 dark:text-white">
            <Pill className="w-5 h-5" />
            Regular Medications
          </CardTitle>
          <CardDescription className="dark:text-gray-400">Your daily medications</CardDescription>
        </CardHeader>
        <CardContent>
          {regularMeds.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Pill className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No medications added yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {regularMeds.map((med) => (
                <div 
                  key={med.id} 
                  className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    med.taken_today 
                      ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' 
                      : 'bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleToggleTaken(med)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        med.taken_today 
                          ? 'bg-emerald-500 text-white' 
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-500'
                      }`}
                    >
                      {med.taken_today && <Check className="w-4 h-4" />}
                    </button>
                    <div>
                      <p className={`font-semibold ${med.taken_today ? 'text-emerald-700 dark:text-emerald-400' : 'text-gray-900 dark:text-white'}`}>
                        {med.medication_name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {med.dosage} • {med.schedule_time} • {med.frequency}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleDeleteMedication(med.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Insulin Schedule */}
      <Card className="shadow-md border-0 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 dark:text-white">
            <Syringe className="w-5 h-5" />
            Insulin Schedule
          </CardTitle>
          <CardDescription className="dark:text-gray-400">Your insulin reminders</CardDescription>
        </CardHeader>
        <CardContent>
          {insulinMeds.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Syringe className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No insulin reminders added yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {insulinMeds.map((med) => (
                <div 
                  key={med.id} 
                  className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    med.taken_today 
                      ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800' 
                      : 'bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleToggleTaken(med)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        med.taken_today 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-500'
                      }`}
                    >
                      {med.taken_today && <Check className="w-4 h-4" />}
                    </button>
                    <div>
                      <p className={`font-semibold ${med.taken_today ? 'text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                        {med.medication_name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {med.dosage} • {med.schedule_time} • {med.frequency}
                        {med.insulin_type && ` • ${med.insulin_type}-acting`}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleDeleteMedication(med.id)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MedicationReminders;
